import React, { useRef, useState, useEffect } from 'react'
import { StaticGrid } from 'components/common'
import { useDispatch, useSelector } from 'react-redux'
import { selectEpisodeDraftHasTransmissions,selectAudiovisualSort,selectEpisodeResultPresentation, selectEpisodeResultOriginal, selectProgrammeGuid } from 'components/Body/data-entry/AudioVisual/store/selectors'
import { setAudiovisualSortAction, setEpisodeDraftResultsTransmissionsAction, clearEpisodeDraftResultsTransmissionsAction, undoClearEpisodeDraftResultsTransmissionsAction } from 'components/Body/data-entry/AudioVisual/store/actions'
import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import { selectAllAssets } from 'providers/AssetLoader/store/selectors'
import { selectAvApiHost } from 'globalSelectors'
import * as Xlsx from 'xlsx'
import { ExportFormat } from 'modules/Constants'
import { sanitizeExportData } from 'modules/exportSanitizer'
import { formatDateWithOffset } from 'modules/formatters'
import { createToggleDeleteHandler } from 'components/common/CommonGrids/utils/createToggleDeleteHandler'

export function Transmissions(props) {
    const dispatch = useDispatch()
    const setAcceptedTransmission = (transmission) => dispatch(setEpisodeDraftResultsTransmissionsAction(transmission))
    const clearTransmission = (transmission) => dispatch(clearEpisodeDraftResultsTransmissionsAction(transmission))
    const undoClearTransmission = (transmission) => dispatch(undoClearEpisodeDraftResultsTransmissionsAction(transmission))
    const toast = React.useRef(null)
    const childRef = useRef()
    const addCounterRef = useRef(0)
    const episodeDataOriginal = useSelector(selectEpisodeResultOriginal)
    const episodeData = useSelector(selectEpisodeResultPresentation)
    const programmeId = useSelector(selectProgrammeGuid)
    const { channels } = useSelector(selectAllAssets)
    const avHost = useSelector(selectAvApiHost)
    const sort = useSelector(selectAudiovisualSort)
    const hasTransmissionsModified = useSelector(selectEpisodeDraftHasTransmissions)
    const allTransmissions = episodeData && episodeData.transmissions ? episodeData.transmissions : []
    const transmissionsOriginal = episodeDataOriginal && episodeDataOriginal.transmissions ? episodeDataOriginal.transmissions : []
    const isFilm = props.isFilm
    const excelDownloadName = 'Transmissions.xlsx'
    const excelDownloadPath = `${avHost}/Programme/${programmeId}/Episodes/${episodeData.id}/TransmissionsExcelExport`
    const isTransmissionsOriginalEmpty = !transmissionsOriginal || Object.keys(transmissionsOriginal).length === 0


    const [scrollHeight, setScrollHeight] = useState(calculateScrollHeight())
  
    //Andy L approves this useEffect
    useEffect(() => {
        const handleResize = () => {
            setScrollHeight(calculateScrollHeight())
        }
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    //TODO: We should not be changing styles in JS. This should be done in CSS. The staticcommonGrid doesnot allow for this to be done in CSS
    function calculateScrollHeight() {
        const adjustedHeight = window.innerHeight / window.devicePixelRatio
        let height = adjustedHeight * 0.2
        if (!isFilm) {
            height = height - 30
        }
        return `${height}px`
    }   

  
    const transformTransmissions = (transmissions) => {
        const transformedTransmissions = Object.values(transmissions).map((d) => ({
            ...d,
            date: new Date(d.date),
            time: new Date(d.date),
            channel: channels.find(c => c.id === d.channelId)
        }))
        transformedTransmissions.sort((a, b) => b.date - a.date)
        return transformedTransmissions
    }

    const transformTransmissionsForExcel = (transmissions) => {
        const transformedTransmissions = Object.values(transmissions).map((d) => {
            const dateObj = new Date(d.date)
            const date = dateObj.toLocaleDateString('en-GB') // Format date as dd/mm/yyyy
            const time = dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) // Format time as hh:mm
            const channel = channels.find(c => c.id === d.channelId)?.name || ''

            return {
                ...d,
                date,
                time,
                channel
            }
        })
        return transformedTransmissions
    }


    const handleAddTransmission = () => {
        if (childRef.current) {
            addCounterRef.current++
            childRef.current.addNewRow({ _editKey: addCounterRef.current })
        }
    }

    // Handle toggle delete transmission using shared utility
    const handleToggleDeleteTransmission = (rowData) => {
        createToggleDeleteHandler({
            gridRef: childRef,
            dataKey: 'sapLineNum',
            originalData: transmissionsOriginal,
            transformOriginal: (originalItem, isDeleted) => ({
                ...originalItem,
                date: new Date(originalItem.date),
                time: new Date(originalItem.date),
                channel: channels.find(c => c.id === originalItem.channelId),
                isDeleted
            }),
            onDelete: clearTransmission,
            onUndoDelete: undoClearTransmission,
            onDeleteComplete: (keyValue) => {
                // Clean up channel selection state when row is deleted
                // This prevents stale channel values from appearing in new rows
                if (childRef.current?.clearChannelSelection) {
                    childRef.current.clearChannelSelection(keyValue)
                }
            }
        })(rowData)
    }


    const validateData = (rowData) => {
        const errors = {}
        if (!rowData.date || rowData.date === '') errors.date = 'Date is mandatory'
        if (!rowData.time || rowData.time === '') errors.time = 'Time is mandatory'
        if (!rowData.channel || rowData.channel === '') errors.channel = 'Channel is mandatory'
        return errors
    }

    const handleRowEditSave = (rowData) => {
        const { channel, date, time, _editKey, ...rest } = rowData
        date.setHours(time.getHours(), time.getMinutes(), 0, 0)
        time.setFullYear(date.getFullYear(), date.getMonth(), date.getDate()) 

        const updatedRowData = {
            ...rest,
            date: formatDateWithOffset(date),
            channelId: channel.id
        };

        (transmissionsOriginal && JSON.stringify(transmissionsOriginal) === JSON.stringify(updatedRowData)) ? clearTransmission(rowData.sapLineNum) : setAcceptedTransmission({ [rowData.sapLineNum]: updatedRowData })
    }

    const applySort = (data) => {
        const dataArray = Array.isArray(data) ? data : Object.values(data)

        if (!sort || sort.length === 0) {
            return dataArray.sort((a, b) => new Date(b.date) - new Date(a.date))
        }
                
        return dataArray.sort((a, b) => {
            for (let i = 0; i < sort.length; i++) {
                const { field, order } = sort[i]
                let result = 0
                if (field === 'date') {                    
                    const dateA = new Date(a.date)
                    const dateB = new Date(b.date)
                    result = dateA - dateB
                }           
                if (result !== 0) {
                    return order * result
                }
            }
            return 0
        })
    }

    const handleExport = (format) => {
        const sortedData = applySort(allTransmissions)
        const dataToExport = transformTransmissionsForExcel(sortedData).map(({ sapLineNum, channelId, ...rest }) => rest)

        // Capitalize the first letter of each column name
        const capitalizedDataToExport = dataToExport.map(item =>
            Object.fromEntries(Object.entries(item).map(([key, value]) => [key.charAt(0).toUpperCase() + key.slice(1), value]))
        )

        const sanitizedData = sanitizeExportData(capitalizedDataToExport)

        if (format === ExportFormat.Excel) {
            const worksheet = Xlsx.utils.json_to_sheet(sanitizedData)
            const workbook = Xlsx.utils.book_new()
            Xlsx.utils.book_append_sheet(workbook, worksheet, 'Transmissions')
            Xlsx.writeFile(workbook, excelDownloadName)
        } else if (format === ExportFormat.Csv) {
            const worksheet = Xlsx.utils.json_to_sheet(sanitizedData)
            const csvOutput = Xlsx.utils.sheet_to_csv(worksheet)
            const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = excelDownloadName.replace('.xlsx', '.csv') // Ensure filename is CSV
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        }
    }

    const header = (
        <div className="d-flex justify-content-end">
            <Button id={props.id + '_container_transmission_dataTable_export_csv'} className="mr-3 justify-content-end" label="CSV Export" icon="fa-light fa-file-import" onClick={() => handleExport(ExportFormat.Csv)} disabled={hasTransmissionsModified || isTransmissionsOriginalEmpty } /> 
            <Button id={props.id + '_container_transmission_dataTable_export_excel'} className="mr-3" label="Excel Export" icon="fa-light fa-file-import" onClick={() => handleExport(ExportFormat.Excel)} disabled={hasTransmissionsModified || isTransmissionsOriginalEmpty } />
            <Button id={props.id + '_container_transmission_add'} label="Add Transmission" icon="fa-light fa-plus" onClick={handleAddTransmission} />
        </div>
    )
    const displayActions = function (rowData, options) {
        // Hide delete button when row is in edit mode
        const isEditing = options?.isEditing

        return (
            <div id={props.id + '_avList_TransmissionGrid_actions'} className="d-flex justify-content-start align-items-center">
                {!isEditing && (
                    <Button className="ml-2" icon={rowData.isDeleted ? "fa-light fa-trash-slash" : "fa-light fa-trash"} rounded
                        onClick={() => handleToggleDeleteTransmission(rowData)} />
                )}
            </div>
        )
    }

    const transmissionColumnDefinitions = [
        { columnType: 'date', title: 'Date', mapsToField: 'date', filter: false, filterMatchMode: FilterMatchMode.DATE_IS, sortable: true, editable: true },
        { columnType: 'time', title: 'Time', mapsToField: 'time', filterMatchMode: FilterMatchMode.EQUALS, filter: false, sortable: false, editable: true },
        { columnType: 'channel', title: 'Channel', mapsToField: 'channel', editable: true },
        { columnType: 'actions', template: displayActions }
    ]

    // Custom key generator for sapLineNum - finds max and increments
    const generateSapLineNum = (dataKey, localData, originalData) => {
        const maxLineNumber = Math.max(
            ...(localData.map(row => row[dataKey] || 0)),
            ...(originalData ? Object.values(originalData).map(row => row[dataKey] || 0) : []),
            ...(allTransmissions ? Object.values(allTransmissions).map(row => row[dataKey] || 0) : []),
            0
        )
        return maxLineNumber + 1
    }

    // Custom cancel logic for channel field restoration
    const handleRowCancelCustom = (rowData, data, selectedChannels, setSelectedChannels) => {
        const newDataKey = rowData.sapLineNum
        const existingRow = data.find(row => row.sapLineNum === newDataKey)

        if (existingRow) {
            // Existing row - restore original channel
            const selectedChannel = existingRow.channel?.name || ''
            const newSelectedChannels = {
                ...selectedChannels.current,
                [newDataKey]: selectedChannel
            }
            setSelectedChannels(newSelectedChannels)
        } else {
            // New row being canceled - remove from selectedChannels to prevent stale state
            const { [newDataKey]: _, ...remainingChannels } = selectedChannels.current
            setSelectedChannels(remainingChannels)
        }
    }

    return (
        <div id={props.id + '_transmissions'} >
            <Toast ref={toast} />
            <StaticGrid
                id={'transmissions'}
                episodeId={episodeData.id}
                dataKey="sapLineNum"
                ref={childRef}
                scrollHeight={scrollHeight}
                data={transformTransmissions(allTransmissions)}
                originalData={transformTransmissions(transmissionsOriginal)}
                totalCount={transformTransmissions(allTransmissions).length}
                emptyMessage="No Transmissions"
                columnDefinitions={transmissionColumnDefinitions}
                showPaginator={false}
                header={header}
                excelDownloadPath={excelDownloadPath}
                excelDownloadName={excelDownloadName}
                allowRowEdits={true}
                validateData={validateData}
                onSave={handleRowEditSave}
                setSortAction={setAudiovisualSortAction}
                draftAvailable={hasTransmissionsModified}
                generateNewRowKey={generateSapLineNum}
                onRowCancelCustom={handleRowCancelCustom}
                toastRef={toast}
            />
        </div>
    )
}
